import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  getAIDashboards,
  getAIDashboardsDetails,
  updateAIDashboard,
} from "../queries/ai.graphql";
import { useWorkspaceFeatureFlags } from "../contexts/global/WorkspaceContext";
import { EventBus, EVENTS } from "../services/eventEmitter";
import decodeHTMLEntities from "../util/decodeHTMLEntities";
import isUnknownFieldError from "../util/isUnknownFieldError";

const LIST_QUERIES = [getAIDashboards, getAIDashboardsDetails];

const mapDashboards = (dashboardsData, detailsById) => {
  if (!dashboardsData?.dashboards?.length) {
    return [];
  }

  return dashboardsData.dashboards
    .map((dashboard) => {
      const details = detailsById.get(dashboard.id);
      return {
        ...dashboard,
        ...(details
          ? {
              isPinned: details.isPinned,
              sourceLibraryItemId: details.sourceLibraryItemId,
              categories: details.categories,
            }
          : {}),
        title: decodeHTMLEntities(dashboard.title),
        createdAt: new Date(parseInt(dashboard.createdAt, 10)),
        updatedAt: new Date(parseInt(dashboard.updatedAt, 10)),
      };
    })
    .sort((a, b) => {
      if (Boolean(a.isPinned) !== Boolean(b.isPinned)) {
        return a.isPinned ? -1 : 1;
      }
      return b.createdAt - a.createdAt;
    })
    .map(({ messageIndex, ...dashboard }) => dashboard);
};

/**
 * Hook to extract all AI-created dashboards from chat history
 * @returns {Record<string, any>} { dashboards: Array, loading: boolean, error: Error, selectedDashboardId: string }
 */
const useAIDashboards = ({ skipQueries = false } = {}) => {
  const { enableAIChat } = useWorkspaceFeatureFlags();
  const [detailsUnsupported, setDetailsUnsupported] = useState(false);
  const skipList = skipQueries || !enableAIChat;

  const core = useQuery(getAIDashboards, {
    fetchPolicy: "cache-and-network",
    skip: skipList,
  });
  const details = useQuery(getAIDashboardsDetails, {
    fetchPolicy: "cache-and-network",
    skip: skipList || detailsUnsupported,
  });
  useEffect(() => {
    if (details.error && isUnknownFieldError(details.error)) {
      setDetailsUnsupported(true);
    }
  }, [details.error]);

  const listQueries = detailsUnsupported ? [getAIDashboards] : LIST_QUERIES;

  const [updateDashboard] = useMutation(updateAIDashboard, {
    refetchQueries: listQueries,
    awaitRefetchQueries: true,
  });

  const renameDashboard = useCallback(
    (id, title) => updateDashboard({ variables: { input: { id, title } } }),
    [updateDashboard]
  );

  /** Soft delete: the dashboard stays in the database but the user stops seeing it. */
  const hideDashboard = useCallback(
    (id) => updateDashboard({ variables: { input: { id, isHidden: true } } }),
    [updateDashboard]
  );

  const setDashboardLibrarySource = useCallback(
    (id, sourceLibraryItemId, categories) =>
      updateDashboard({
        variables: {
          input: {
            id,
            sourceLibraryItemId,
            ...(Array.isArray(categories) ? { categories } : {}),
          },
        },
      }),
    [updateDashboard]
  );

  const updateDashboardCategories = useCallback(
    (id, categories) =>
      updateDashboard({ variables: { input: { id, categories } } }),
    [updateDashboard]
  );

  const detailsById = useMemo(() => {
    const byId = new Map();
    (details.data?.dashboards || []).forEach((dashboard) => {
      byId.set(dashboard.id, dashboard);
    });
    return byId;
  }, [details.data]);

  const dashboards = useMemo(
    () => mapDashboards(core.data, detailsById),
    [core.data, detailsById]
  );

  const pinDashboard = useCallback(
    (id, isPinned) => {
      const current = dashboards.find((dashboard) => dashboard.id === id);
      return updateDashboard({
        variables: { input: { id, isPinned } },
        optimisticResponse: {
          updateAIDashboard: {
            __typename: "AIDashboard",
            id,
            title: current?.title ?? "",
            isHidden: false,
          },
        },
        update(cache, { data }) {
          if (!data?.updateAIDashboard) {
            return;
          }
          cache.updateQuery({ query: getAIDashboardsDetails }, (existing) => {
            if (!existing?.dashboards) {
              return existing;
            }
            return {
              ...existing,
              dashboards: existing.dashboards.map((dashboard) =>
                dashboard.id === id ? { ...dashboard, isPinned } : dashboard
              ),
            };
          });
        },
      });
    },
    [dashboards, updateDashboard]
  );

  const refetchCore = core.refetch;
  const refetchDetails = details.refetch;
  const refetch = useCallback(() => {
    return Promise.all([
      refetchCore ? refetchCore() : Promise.resolve(),
      !detailsUnsupported && refetchDetails
        ? refetchDetails()
        : Promise.resolve(),
    ]);
  }, [refetchCore, refetchDetails, detailsUnsupported]);

  useEffect(() => {
    if (!enableAIChat || !refetch) {
      return undefined;
    }
    const onDashboardUpdated = () => {
      void refetch();
    };
    EventBus.on(EVENTS.AI_DASHBOARD_UPDATED, onDashboardUpdated);
    return () => {
      EventBus.off(EVENTS.AI_DASHBOARD_UPDATED, onDashboardUpdated);
    };
  }, [enableAIChat, refetch]);

  return {
    dashboards,
    loading: core.loading,
    error: core.error,
    refetch,
    renameDashboard,
    hideDashboard,
    pinDashboard,
    setDashboardLibrarySource,
    updateDashboardCategories,
  };
};

export default useAIDashboards;
